import React, { useEffect, useRef, useState } from "react";

/**
 * Polite live region that announces status-level text changes to assistive
 * technology (screen readers). Use for ephemeral state transitions like
 * "Two-step highlight: 4 related edges shown" that do not fit into the
 * flow of a visual toast but should still reach non-visual users.
 *
 * The component renders an `sr-only` block with `role="status"` and
 * `aria-live="polite"` so announcements do not interrupt the user's current
 * focus. It avoids firing on first mount (so a static message does not
 * announce itself on page load) by only announcing after the `message` prop
 * changes.
 */
export interface LiveAnnouncerProps {
  /**
   * The current announcement message. Setting a new non-empty value causes
   * assistive tech to read it. Re-setting the same value does not re-announce.
   */
  message: string;
  /**
   * Milliseconds to keep the message visible in the live region before
   * clearing it. Shorter clear times let consecutive identical messages be
   * re-announced (some screen readers dedupe within a single aria-live
   * payload). Defaults to 1500ms.
   */
  clearAfterMs?: number;
  /**
   * Priority level for assistive tech. Defaults to "polite"; "assertive"
   * should only be used for critical errors that must interrupt.
   */
  politeness?: "polite" | "assertive";
}

const LiveAnnouncer: React.FC<LiveAnnouncerProps> = ({
  message,
  clearAfterMs = 1500,
  politeness = "polite",
}) => {
  const [announcedMessage, setAnnouncedMessage] = useState<string>("");
  const firstRender = useRef(true);
  const lastMessageRef = useRef<string>("");

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      lastMessageRef.current = message;
      return undefined;
    }
    if (message === lastMessageRef.current) {
      return undefined;
    }
    lastMessageRef.current = message;
    setAnnouncedMessage(message);
    if (!message) {
      return undefined;
    }
    const clearTimer = window.setTimeout(() => {
      setAnnouncedMessage("");
    }, clearAfterMs);
    return () => {
      window.clearTimeout(clearTimer);
    };
  }, [clearAfterMs, message]);

  return (
    <div
      role="status"
      aria-live={politeness}
      aria-atomic="true"
      className="sr-only"
    >
      {announcedMessage}
    </div>
  );
};

export default LiveAnnouncer;
